import { Suspense, lazy, useMemo } from 'react'
import { MDXProvider } from '@mdx-js/react'
import { Link, useParams } from 'react-router'
import { Section, SectionWrapper } from '@/components/layout/Section'
import { BottomCTA } from '@/components/sections'
import { Prose } from '@/components/content/Prose'
import { mdxComponents } from '@/components/content/mdx-components'
import { Badge } from '@/components/ui/badge'
import { POSTS } from '@/content/docs.generated'
import { loadDoc } from '@/content/loader'
import { NotFound } from '@/routes/NotFound'
import { RelatedPosts } from '@/components/content/RelatedPosts'
import { ShareBar } from '@/components/content/ShareBar'
import { Seo } from '@/components/seo/Seo'
import { ArticleJsonLd, BreadcrumbJsonLd } from '@/components/seo/JsonLd'
import { formatDocDate } from '@/lib/date'

/**
 * `/post/:slug` — the blog post template, one component for all 17 posts.
 *
 * The capture had these as 17 near-identical pages differing only in content;
 * everything structural lives here now.
 */
export function PostPage() {
  const { slug } = useParams()
  const doc = POSTS.find((p) => p.slug === slug)

  const Body = useMemo(() => {
    if (!doc) return null
    const loader = loadDoc(doc.route)
    return loader ? lazy(async () => ({ default: (await loader()).default })) : null
  }, [doc])

  if (!doc) return <NotFound />

  return (
    <>
      <Seo
        title={doc.seoTitle}
        description={doc.description}
        path={doc.route}
        image={doc.ogImage ?? doc.heroImage}
        type="article"
        publishedTime={doc.date}
        author={doc.author}
      />
      <ArticleJsonLd doc={doc} />
      <BreadcrumbJsonLd
        items={[
          { name: 'Blog', path: '/blog' },
          { name: doc.title, path: doc.route },
        ]}
      />
      {/*
        Hero runs at the full 1240px column on a white ground; only the body
        narrows to 740px. Rhythm comes from the wrapper's 32px gap, so no
        element carries its own top margin.
      */}
      <Section className="post-hero">
        <SectionWrapper layout="stack" gap="gap">
          <Link to="/blog" className="post-hero__back">
            <img src="/media/66febea3b539816be9049e98_arrowblueleft.svg" alt="" />
            <span className="post-hero__back-label">All posts</span>
          </Link>
          <div className="post-hero__head">
            {doc.category && <Badge>{doc.category}</Badge>}
            <h1 className="post-hero__title">{doc.title}</h1>
            {doc.description && <p className="post-hero__desc text-large">{doc.description}</p>}
            <div className="post-hero__meta">
              <div className="post-hero__meta-row">
                <div className="post-hero__author">
                  {doc.authorAvatar && (
                    <img
                      src={doc.authorAvatar}
                      alt=""
                      loading="lazy"
                      className="author-avatar"
                    />
                  )}
                  {doc.author && <span>{doc.author}</span>}
                </div>
                {doc.author && doc.date && <span aria-hidden="true" className="post-hero__dot" />}
                {doc.date && (
                  <div className="post-hero__date">
                    <img src="/media/66fe893e39af7f59ddd9f970_calendar.svg" alt="" />
                    <time dateTime={doc.date}>{formatDocDate(doc.date)}</time>
                  </div>
                )}
              </div>
            </div>
          </div>
          {doc.heroImage && doc.heroInPost !== false && (
            <img src={doc.heroImage} alt="" className="post-hero__image" />
          )}
          <ShareBar path={doc.route} />
        </SectionWrapper>
      </Section>

      <Section className="post-body">
        <SectionWrapper layout="stack" width="blogPost">
          <MDXProvider components={mdxComponents}>
            <Prose>
              <Suspense
                fallback={<p className="font-mono text-sm text-font-secondary">Loading…</p>}
              >
                {Body && <Body />}
              </Suspense>
            </Prose>
          </MDXProvider>
        </SectionWrapper>
      </Section>

      <RelatedPosts currentSlug={doc.slug} category={doc.category} slugs={doc.related} />
      <BottomCTA />
    </>
  )
}
